import React from 'react';
import { timelineColors } from './tokens';

// Inline SVG glyphs, ported verbatim (viewBox + path geometry) from the
// design prototype so the icon language matches the handoff exactly.
// `foreground` should come from `getReadableForeground(color)` — actor
// colors are dynamically assigned and can land on light palette entries
// where a hardcoded white glyph would be invisible.

export const GLYPH_SIZE = 22;
const VIEWBOX = '0 0 24 24';

const sanitizeColorId = (color: string) => color.replace(/[^a-zA-Z0-9]/g, '');

export const arrowMarkerId = (color: string) => `et-arrow-${sanitizeColorId(color)}`;
export const gradientRingId = (color: string) => `et-vsring-${sanitizeColorId(color)}`;
export const gradientFadeId = (color: string) => `et-vsfade-${sanitizeColorId(color)}`;

// Mounted once per canvas render, deduped by the distinct colors actually
// present (not a fixed ACTORS map, since actor colors are dynamic).
export const TimelineSvgDefs = ({ colors }: { colors: string[] }) => {
  const unique = Array.from(new Set(colors));
  return (
    <defs>
      {unique.map((c) => (
        <marker
          key={`marker-${c}`}
          id={arrowMarkerId(c)}
          markerWidth={4}
          markerHeight={4}
          refX={3}
          refY={2}
          orient="auto"
        >
          <path d="M0,0 L4,2 L0,4 Z" fill={c} />
        </marker>
      ))}
      {unique.map((c) => (
        <linearGradient
          key={`ring-${c}`}
          id={gradientRingId(c)}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor={c} stopOpacity={1} />
          <stop offset="100%" stopColor={c} stopOpacity={0.4} />
        </linearGradient>
      ))}
      {unique.map((c) => (
        <linearGradient
          key={`fade-${c}`}
          id={gradientFadeId(c)}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          <stop offset="0%" stopColor={c} stopOpacity={1} />
          <stop offset="100%" stopColor={c} stopOpacity={0} />
        </linearGradient>
      ))}
    </defs>
  );
};

type GlyphProps = {
  color: string;
  foreground: string;
  variableStart?: boolean;
};

export const MessageGlyph = ({ color, foreground, variableStart }: GlyphProps) => {
  const ring = variableStart ? `url(#${gradientRingId(color)})` : undefined;
  const glyphColor = variableStart ? `url(#${gradientRingId(color)})` : foreground;
  const fill = variableStart ? timelineColors.surface : color;
  return (
    <svg viewBox={VIEWBOX} width={GLYPH_SIZE} height={GLYPH_SIZE}>
      <circle cx={12} cy={12} r={11} fill={fill} stroke={ring} strokeWidth={ring ? 1.6 : 0} />
      <rect x={5.5} y={8} width={13} height={9} rx={1.5} stroke={glyphColor} strokeWidth={1.6} fill="none" />
      <path
        d="M6 9l6 4.2L18 9"
        stroke={glyphColor}
        strokeWidth={1.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const TaskStartGlyph = ({ color, foreground, variableStart }: GlyphProps) => {
  const ring = variableStart ? `url(#${gradientRingId(color)})` : undefined;
  const glyphColor = variableStart ? `url(#${gradientRingId(color)})` : foreground;
  const fill = variableStart ? timelineColors.surface : color;
  return (
    <svg viewBox={VIEWBOX} width={GLYPH_SIZE} height={GLYPH_SIZE}>
      <circle cx={12} cy={12} r={11} fill={fill} stroke={ring} strokeWidth={ring ? 1.6 : 0} />
      <path
        d="M7.5 12.5l2.8 2.8L16.5 9"
        stroke={glyphColor}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Automations have no `variableStart` concept in the adapter (they're always
// fixed or plain-triggered), so no ring variant.
export const AutomationGlyph = ({ color, foreground }: Omit<GlyphProps, 'variableStart'>) => (
  <svg viewBox={VIEWBOX} width={GLYPH_SIZE} height={GLYPH_SIZE}>
    <circle cx={12} cy={12} r={11} fill={color} />
    <path d="M13.2 4.5L7 13h4.2L10 19.5 17 11h-4.2z" fill={foreground} />
  </svg>
);

// Not a circle — a 3px rounded vertical bar placed at the task's end-day
// column, connected to the start glyph by a horizontal line (drawn by the
// caller, since it needs the day-range in pixels).
export const TaskEndBar = ({
  color,
  height = 21,
  onPointerDown
}: {
  color: string;
  height?: number;
  onPointerDown?: (ev: React.PointerEvent) => void;
}) => (
  <div
    onPointerDown={onPointerDown}
    style={{
      width: 3,
      height,
      background: color,
      borderRadius: 1.5,
      cursor: onPointerDown ? 'e-resize' : undefined
    }}
  />
);
